/**
 * GA4（Google Analytics 4）計測。
 *
 * 設計:
 * - gtag.js は index.html にインラインで置かず、このモジュールから動的に注入する。
 *   CSP の script-src に 'unsafe-inline' を足さずに済ませるため（追加するのは
 *   https://www.googletagmanager.com のみ。正本: apps/web/public/_headers）。
 * - Consent Mode v2 の既定は全カテゴリ denied。Cookie 同意 UI（analytics カテゴリ）が
 *   ON になったときだけ analytics_storage を granted へ更新する。denied の間も
 *   Cookie を使わない ping は送られるため、同意前の訪問も集計には反映される。
 * - 本番ビルドでのみ有効。localhost の開発トラフィックを本番プロパティに混ぜない。
 */

/** GA4 測定 ID（公開値。プロパティ「AI Deck」/ ストリーム https://ai-deck.app） */
export const GA_MEASUREMENT_ID = "G-XPD2THMD9T";

declare global {
  interface Window {
    dataLayer?: IArguments[];
    gtag?: (...args: unknown[]) => void;
  }
}

const isAnalyticsEnabled = import.meta.env.PROD;

let isInitialized = false;

/** gtag.js の仕様どおり arguments オブジェクトをそのまま dataLayer に積むスタブを用意する */
function ensureGtag(): (...args: unknown[]) => void {
  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtagStub() {
      // biome-ignore lint/complexity/noArguments: gtag.js は dataLayer に arguments オブジェクトが積まれる前提で動くため、rest 引数の配列に置き換えられない
      window.dataLayer?.push(arguments);
    };
  }
  return window.gtag;
}

/**
 * GA4 を初期化する（アプリ起動時に 1 回だけ）。
 * 同意状態は読み込みが非同期なため、ここでは常に denied で開始し、
 * 確定後に updateAnalyticsConsent() で引き上げる（wait_for_update で取りこぼしを防ぐ）。
 */
export function initAnalytics(): void {
  if (!isAnalyticsEnabled || isInitialized) return;
  isInitialized = true;

  const gtag = ensureGtag();

  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });

  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    // SPA のためルート遷移は trackPageView() で明示送信する（初回のみ自動送信に任せる）
    send_page_view: true,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/** Cookie 同意（analytics カテゴリ）の変化を GA に伝える */
export function updateAnalyticsConsent(isGranted: boolean): void {
  if (!isAnalyticsEnabled || !isInitialized) return;

  window.gtag?.("consent", "update", {
    analytics_storage: isGranted ? "granted" : "denied",
  });
}

/** SPA のルート遷移を page_view として送信する */
export function trackPageView(path: string): void {
  if (!isAnalyticsEnabled || !isInitialized) return;

  window.gtag?.("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}
